import React, { useMemo } from 'react';
import { Table, Card, Row, Col, Badge, Icon } from 'antd';

const pulseStyles = `
@keyframes icon-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-4px); }
}
@keyframes icon-beat {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.22); }
}
@keyframes icon-shake {
  0%, 100% { transform: rotate(0deg); }
  20%       { transform: rotate(-14deg); }
  40%       { transform: rotate(14deg); }
  60%       { transform: rotate(-8deg); }
  80%       { transform: rotate(8deg); }
}
@keyframes icon-spin-slow {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes icon-bounce {
  0%, 100% { transform: translateY(0px) scaleY(1); }
  40%       { transform: translateY(-5px) scaleY(1.05); }
  70%       { transform: translateY(2px) scaleY(0.96); }
}
@keyframes icon-swing {
  0%, 100% { transform: rotate(0deg); }
  25%       { transform: rotate(10deg); }
  75%       { transform: rotate(-10deg); }
}
@keyframes glow-ring {
  0%, 100% { box-shadow: 0 0 0 0 var(--glow-color, rgba(24,144,255,0.35)); }
  50%       { box-shadow: 0 0 0 8px rgba(0,0,0,0); }
}

.mat-float  { animation: icon-float     3s    ease-in-out infinite; }
.mat-beat   { animation: icon-beat      1.8s  ease-in-out infinite; }
.mat-shake  { animation: icon-shake     2.6s  ease-in-out infinite; }
.mat-spin   { animation: icon-spin-slow 7s    linear      infinite; }
.mat-bounce { animation: icon-bounce    2.2s  ease-in-out infinite; }
.mat-swing  { animation: icon-swing     2.4s  ease-in-out infinite; }
.mat-glow   { animation: glow-ring      2.4s  ease-in-out infinite; }

.mat-row-stripe td { background: #f7f9ff !important; }
`;

const MaterialPublicTable = (props) => {
	const { itemList } = props;

	// Compute statistics for public view
	const stats = useMemo(() => {
		if (!itemList || itemList.length === 0) return {
			total: 0,
			customers: 0,
			totalRequiredQty: 0,
			totalOuts: 0,
		};
		const customers = new Set(itemList.map(i => i.customer_label)).size;
		const totalRequiredQty = itemList.reduce((sum, item) => sum + (parseFloat(item.requiredqty) || 0), 0);
		const totalOuts = itemList.reduce((sum, item) => sum + (parseFloat(item.outs) || 0), 0);
		return { 
			total: itemList.length, 
			customers, 
			totalRequiredQty, 
			totalOuts 
		};
	}, [itemList]);

	const cardStyle = {
		borderRadius: 10,
		boxShadow: '0 2px 12px rgba(24,144,255,0.08)',
		border: '1px solid #e6f0ff',
		background: '#fff',
		height: '100%',
	};

	const statCards = [
		{
			title: 'Total Materials',
			value: stats.total,
			icon: 'database',
			color: '#1890ff',
			bg: '#e6f7ff',
			anim: 'mat-float',
			glow: 'rgba(24,144,255,0.3)',
		},
		{
			title: 'Customers',
			value: stats.customers,
			icon: 'team',
			color: '#13c2c2',
			bg: '#e6fffb',
			anim: 'mat-beat',
			glow: 'rgba(19,194,194,0.3)',
		},
		{
			title: 'Total Required Qty',
			value: stats.totalRequiredQty.toLocaleString(),
			icon: 'shopping-cart',
			color: '#fa8c16',
			bg: '#fff7e6',
			anim: 'mat-swing',
			glow: 'rgba(250,140,22,0.3)',
		},
		{
			title: 'Total Outs',
			value: stats.totalOuts.toLocaleString(),
			icon: 'export',
			color: '#eb2f96',
			bg: '#fff0f6',
			anim: 'mat-bounce',
			glow: 'rgba(235,47,150,0.3)',
		},
	];

	const columns = [
		{
			key: 'epcode',
			title: 'CODE',
			dataIndex: 'code',
			sorter: (a, b) => a.code.localeCompare(b.code),
			sortDirections: ['ascend', 'descend'],
			render: (data) => <span style={{ fontWeight: 600, color: '#1890ff' }}>{data?.toString().toUpperCase()}</span>
		},
		{
			key: 'mspecs',
			title: 'MATERIAL SPECIFICATION',
			dataIndex: 'mspecs',
			sorter: (a, b) => a.mspecs.localeCompare(b.mspecs),
			sortDirections: ['ascend', 'descend'],
			render: (data) => data?.toString().toUpperCase()
		},
		{
			key: 'itemdesc',
			title: 'ITEM DESCRIPTION',
			dataIndex: 'itemdesc',
			sorter: (a, b) => a.itemdesc.localeCompare(b.itemdesc),
			sortDirections: ['ascend', 'descend'],
			render: (data) => data?.toString().toUpperCase()
		},
		{
			key: 'partnum',
			title: 'PART NO.',
			dataIndex: 'partnum',
			sorter: (a, b) => a.partnum.localeCompare(b.partnum),
			sortDirections: ['ascend', 'descend'],
			render: (data) => data?.toString().toUpperCase()
		},
		{
			key: 'requiredqty',
			title: 'REQUIRED QTY',
			dataIndex: 'requiredqty',
			render: (data) => data ? (
				<Badge 
					count={data} 
					style={{ 
						backgroundColor: '#52c41a', 
						fontSize: 11, 
						fontWeight: 600,
						boxShadow: '0 1px 4px rgba(82,196,26,0.3)'
					}} 
				/>
			) : '-'
		},
		{
			key: 'outs',
			title: 'OUTS',
			dataIndex: 'outs',
			render: (data) => data ? (
				<Badge 
					count={data} 
					style={{ 
						backgroundColor: '#ff4d4f', 
						fontSize: 11, 
						fontWeight: 600,
						boxShadow: '0 1px 4px rgba(255,77,79,0.3)'
					}} 
				/>
			) : '-'
		},
		{
			key: 'customer_label',
			title: 'CUSTOMER',
			dataIndex: 'customer_label',
			sorter: (a, b) => a.customer_label.localeCompare(b.customer_label),
			sortDirections: ['ascend', 'descend'],
			render: (data) => (
				<span style={{ 
					backgroundColor: '#e6f7ff', 
					padding: '2px 8px', 
					borderRadius: 4,
					fontSize: 11,
					fontWeight: 600,
					color: '#1890ff',
					display: 'inline-block'
				}}>
					{data?.toString().toUpperCase()}
				</span>
			)
		},
		{
			key: 'remarks',
			title: 'REMARKS',
			dataIndex: 'remarks',
			render: (data) => data ? (
				<span style={{ 
					color: '#8c8c8c',
					fontStyle: 'italic',
					fontSize: 11
				}}>
					{data}
				</span>
			) : <span style={{ color: '#d9d9d9' }}>—</span>
		},
	];

	return (
		<div style={{ padding: '8px 0' }}>
			<style>{pulseStyles}</style>

			{/* Summary Cards */}
			<Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
				{statCards.map((card, idx) => (
					<Col key={idx} xs={12} sm={12} md={6} lg={6}>
						<Card
							style={cardStyle}
							bodyStyle={{ padding: '18px 20px' }}
							bordered={false}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
								<div
									className={`mat-glow ${card.anim}`}
									style={{
										width: 46,
										height: 46,
										borderRadius: 12,
										background: card.bg,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexShrink: 0,
										'--glow-color': card.glow,
									}}
								>
									<Icon type={card.icon} style={{ fontSize: 18, color: card.color }} />
								</div>
								<div style={{ minWidth: 0 }}>
									<div style={{
										fontSize: 11,
										color: '#8c8c8c',
										fontWeight: 500,
										letterSpacing: '0.3px',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}>
										{card.title.toUpperCase()}
									</div>
									<div style={{
										fontSize: 22,
										fontWeight: 700,
										color: card.color,
										lineHeight: 1.2,
									}}>
										{card.value}
									</div>
								</div>
							</div>
						</Card>
					</Col>
				))}
			</Row>

			{/* Table */}
			<Card
				bordered={false}
				style={{
					borderRadius: 10,
					boxShadow: '0 2px 12px rgba(24,144,255,0.07)',
					border: '1px solid #e6f0ff',
				}}
				bodyStyle={{ padding: '0 0 8px 0' }}
			>
				<div style={{
					padding: '14px 20px 12px',
					borderBottom: '1px solid #f0f5ff',
					display: 'flex',
					alignItems: 'center',
					gap: 8,
				}}>
					<Icon type="table" className="mat-beat" style={{ color: '#1890ff', fontSize: 16 }} />
					<span style={{
						fontWeight: 700,
						fontSize: 13,
						color: '#262626',
						letterSpacing: '0.5px',
					}}>
						MATERIALS LIST
					</span>
					<Badge
						count={stats.total}
						style={{
							backgroundColor: '#1890ff',
							fontSize: 11,
							fontWeight: 600,
						}}
						overflowCount={9999}
					/>
				</div>

				<Table
					columns={columns}
					dataSource={itemList}
					bordered={true}
					size="middle"
					scroll={{ x: 'max-content' }}
					style={{ margin: '0' }}
					rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'mat-row-stripe'}
					pagination={{
						pageSizeOptions: ['10', '25', '50', '100', '500'],
						showSizeChanger: true,
						showTotal: (total, range) =>
							`Showing ${range[0]}-${range[1]} of ${total} items`,
					}}
					rowKey="id"
				/>
			</Card>
		</div>
	);
};

export default MaterialPublicTable;